import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchUserSubmissions, withdrawPaper } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionStatus, setActionStatus] = useState({ type: null, message: null });

  useEffect(() => {
    const loadSubmissions = async () => {
      try {
        setLoading(true);
        const data = await fetchUserSubmissions();
        setSubmissions(data);
      } catch (err) {
        console.error('Error loading user submissions:', err);
        setError('Failed to load your submissions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadSubmissions();
  }, []);

  const handleWithdraw = async (paperId) => {
    if (window.confirm('Are you sure you want to withdraw this paper?')) {
      try {
        await withdrawPaper(paperId);
        
        // Update local state
        setSubmissions(submissions.map(paper => 
          paper.id === paperId ? { ...paper, status: 'WITHDRAWN' } : paper
        ));
        
        setActionStatus({ 
          type: 'success', 
          message: 'Paper has been successfully withdrawn.' 
        });
        
        // Clear message after 5 seconds
        setTimeout(() => {
          setActionStatus({ type: null, message: null });
        }, 5000);
      } catch (err) {
        console.error('Error withdrawing paper:', err);
        setActionStatus({ 
          type: 'error', 
          message: 'Failed to withdraw paper. Please try again.' 
        });
      }
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'SUBMITTED':
        return 'bg-info';
      case 'UNDER_REVIEW':
        return 'bg-primary';
      case 'ACCEPTED':
        return 'bg-success';
      case 'REJECTED':
        return 'bg-danger';
      case 'REVISIONS_REQUIRED':
        return 'bg-warning';
      case 'WITHDRAWN':
        return 'bg-secondary';
      default:
        return 'bg-light text-dark';
    }
  };

  return (
    <div className="container py-5">
      <div className="row mb-4">
        <div className="col-md-8">
          <h1>Author Dashboard</h1>
          <p className="lead">
            Welcome, {user?.firstName} {user?.lastName}. Manage your paper submissions here.
          </p>
        </div>
        <div className="col-md-4 text-md-end d-flex align-items-center justify-content-md-end">
          <Link to="/submit-paper" className="btn btn-primary">
            <i className="fas fa-plus me-2"></i> Submit New Paper
          </Link>
        </div>
      </div>

      {actionStatus.message && (
        <div className={`alert ${actionStatus.type === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
          {actionStatus.message}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header">
          <h3 className="mb-0">Your Submissions</h3>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : submissions.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Submission Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map(paper => (
                    <tr key={paper.id}>
                      <td>
                        <Link
                          to={`/paper/${paper.id}`}
                          state={{ from: '/dashboard', fromLabel: 'My Dashboard' }}
                        >
                          {paper.title}
                        </Link>
                      </td>
                      <td>{paper.submittedAt ? new Date(paper.submittedAt).toLocaleDateString() : 'N/A'}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(paper.status)}`}>
                          {paper.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <Link
                            to={`/paper/${paper.id}`}
                            state={{ from: '/dashboard', fromLabel: 'My Dashboard' }}
                            className="btn btn-outline-primary"
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </Link>
                          
                          {paper.status === 'SUBMITTED' && (
                            <button 
                              className="btn btn-outline-danger"
                              onClick={() => handleWithdraw(paper.id)}
                              title="Withdraw Paper"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          )}
                          
                          {paper.status === 'REVISIONS_REQUIRED' && (
                            <Link 
                              to={`/submit-paper?revision=${paper.id}`} 
                              className="btn btn-outline-warning"
                              title="Submit Revision"
                            >
                              <i className="fas fa-edit"></i>
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-5">
              <p className="mb-3">You haven't submitted any papers yet.</p>
              <Link to="/submit-paper" className="btn btn-primary">
                Submit Your First Paper
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-4">
          <div className="card h-100">
            <div className="card-header">
              <h3 className="mb-0">Submission Guidelines</h3>
            </div>
            <div className="card-body">
              <ul className="mb-0">
                <li>Ensure your manuscript follows the journal's formatting guidelines</li>
                <li>Include all required sections (Abstract, Introduction, Methods, Results, Discussion, References)</li>
                <li>Maximum manuscript length is 8,000 words excluding references</li>
                <li>Tables and figures should be included within the main manuscript</li>
                <li>Follow APA 7th edition citation style</li>
                <li>Provide all author details and affiliations</li>
              </ul>
            </div>
            <div className="card-footer">
              <Link to="/about" className="btn btn-outline-primary btn-sm">View Full Guidelines</Link>
            </div>
          </div>
        </div>
        
        <div className="col-md-6 mb-4">
          <div className="card h-100">
            <div className="card-header">
              <h3 className="mb-0">Publication Timeline</h3>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between mb-3">
                <div>Initial Review:</div>
                <div>2-3 weeks</div>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <div>Peer Review Process:</div>
                <div>4-8 weeks</div>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <div>Revision Period:</div>
                <div>2-4 weeks</div>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <div>Final Decision:</div>
                <div>2 weeks</div>
              </div>
              <div className="d-flex justify-content-between">
                <div>Publication After Acceptance:</div>
                <div>Next available issue</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
